import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';

import sweetAlert from 'sweetalert2';
import io from 'socket.io-client';
import { AxiosError } from 'axios';
import { API_URL } from '../constants';
import * as Sentry from '@sentry/browser';

export const serverAddress = API_URL;

export function connectToActiveSocket(animalType: string, callback: Function) {
  const socket = io(`${serverAddress}/io/animals/${animalType}/active`);
  socket.on('connected', callback);
  socket.on('refresh', callback);
  socket.on('error', (err: Error) => {
    Sentry.addBreadcrumb({
      category: 'REQUEST',
      message: `ERROR_FROM_SOCKET_REQUEST`,
      level: Sentry.Severity.Error,
      data: {
        animal_type: animalType,
        error: JSON.stringify(err),
      },
    });
    Sentry.captureException(err);
  });
  return socket;
}

/**
 * Adds a comma to make large numbers more readable
 *
 * @param {Number} A number to add a comma to
 */
export function addCommasToBigNumbers(num: number) {
  const split = num.toString().split('.');
  split[0] = split[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return split.join('.');
}

export function logInToFirebase(email: string, password: string): Promise<string> {
  return new Promise(resolve => {
    firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(() => resolve(''))
      .catch(async error => {
        const errorCode = error.code;
        let errorMessage;
        switch (errorCode) {
          case 'auth/invalid-email': // Thrown if the email address is not valid.
            errorMessage = `The email address entered was not valid!`;
            break;
          case 'auth/user-disabled': // Thrown if the user corresponding to the given email has been disabled.
            errorMessage = `I'm sorry, it looks like your account has been disabled. Contact your supervisor if you believe this is a mistake.`;
            break;
          case 'auth/user-not-found': // Thrown if there is no user corresponding to the given email.
            errorMessage = `I can't find an account for that email address, are you sure you have made one?`;
            break;
          case 'auth/wrong-password': // Thrown if the password is invalid for the given email, or the account corresponding to the email does not have a password set.
            errorMessage = `Sorry, that password is incorrect.`;
            break;
          default:
            errorMessage = `Oops! Something went wrong. Contact your supervisor if this problem persists!`;
        }

        await sweetAlert({
          title: 'Login Error:',
          text: errorMessage,
          type: 'error',
          confirmButtonText: 'Okay',
        });
        resolve(errorMessage);
      });
  });
}

export function logOutOfFirebase() {
  firebase.auth().signOut();
}

/**
 * Shorthand for Firebase.database().ref()
 *
 * @export
 * @param {string} ref
 * @returns Firebase database refference
 */
export function firebaseRef(ref: string) {
  return firebase.database().ref(`/${ref}`);
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function debounce(fn: Function, delay: number) {
  let timer: number | undefined;
  return function(this: any) {
    const args = arguments;
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

export function errorAlert(extra: string | Error = '', override?: string) {
  if (extra instanceof Error) {
    Sentry.captureException(extra);
  }

  return sweetAlert({
    title: 'Error:',
    text:
      override ||
      `Uh oh! Something went wrong. Try refreshing the page and trying again. If this problem persists, let your supervisor know! ${extra}`,
    type: 'error',
    confirmButtonText: `Okay`,
  });
}

export function popupErrorFromServer(err: AxiosError) {
  const message = err.response && typeof err.response.data === 'string' ? err.response.data : err.message;
  Sentry.addBreadcrumb({
    category: 'REQUEST',
    message: `ERROR_FROM_REQUEST`,
    level: Sentry.Severity.Error,
    data: {
      message: message,
      error: JSON.stringify(err),
    },
  });
  Sentry.captureException(err);

  return sweetAlert({
    title: 'Error:',
    text: message,
    type: 'error',
    confirmButtonText: `Okay`,
  });
}

export function infoAlert(message: string) {
  return sweetAlert({
    title: 'Info:',
    text: message,
    confirmButtonText: `Okay`,
  });
}

/**
 * Calls a sweet alert (success varient), with the passed strings
 * @param {string} title
 * @param {string} message
 */
export function successAlert(title = 'Success!', message = 'The action completed successfully!') {
  return sweetAlert({
    title,
    text: message,
    type: 'success',
    confirmButtonText: 'Okay',
  });
}

export const confirmPopupBefore = async (message: string, cb: () => any, alt?: () => any) => {
  const { dismiss } = await sweetAlert({
    title: 'Confirm:',
    text: message,
    type: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, continue.',
    cancelButtonText: 'No, cancel!',
  });

  if (dismiss) {
    if (alt) {
      await alt();
    }
    return false;
  }
  return await cb();
};

export const confirmSuccessAfter = async (message: string, cb: () => any, alt?: (err: Error) => any) => {
  try {
    const result = await cb();

    await sweetAlert({
      title: 'Success!',
      text: message,
      type: 'success',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Great!',
    });

    return result;
  } catch (err) {
    if (alt) {
      return await alt(err);
    }
    return await popupErrorFromServer(err);
  }
};

// /**
//  * Asks the user to confirm their intent is to permenantly remove
//  * this animal. if yes, removes it from the database.
//  *
//  * @export
//  * @param {animal Object} animal
//  */
// export async function deleteAnimal(animal: Animal) {
//   try {
//     await sweetAlert({
//       title: `Delete ${animal.name} ?`,
//       text: `This will remove ${
//         animal.name
//       } from all lists on the app, and free up their aNumber to be used again. Any enrichment data will remain. Are you sure you want to do this?`,
//       type: 'warning',
//       showCancelButton: true,
//       confirmButtonColor: '#3085d6',
//       cancelButtonColor: '#d33',
//       confirmButtonText: 'Yes, delete!',
//       cancelButtonText: 'No, cancel!',
//     });
//   } catch (err) {
//     // User clicked cancel
//     return;
//   }
//   try {
//     const result = await api.animals.delete(animal.aNumber);
//     await sweetAlert({
//       title: `${animal.name} Removed`,
//       text: `${animal.name} has been successfully removed!`,
//       type: 'success',
//       confirmButtonText: 'Okay',
//     });
//     return result;
//   } catch (error) {
//     errorAlert(error.message);
//   }
// }

// /**
//  * Generates a medical data object to send to the server
//  *
//  * @export
//  * @param {animal Object} animal
//  * @param {and opject containing poop and pee score} poopAndPeeScore
//  * @returns Firebase Promise
//  */
// export function prepMedicalData(animal: Animal, poopAndPeeScore: any) {
// 	let timeStamp = new Date();
// 	let parsedDateObj = parseDate(timeStamp);
// 	let milisec = Date.parse(timeStamp);
// 	//creates medical data object to be stored in database
// 	let medicalDataObject = {};
// 	medicalDataObject.aNumber = animal.aNumber;
// 	medicalDataObject.animalName = animal.name;
// 	medicalDataObject.animalType = animal.animalType;
// 	medicalDataObject.walkerName = animal.signOutData.walkerName;
// 	medicalDataObject.walkerType = animal.signOutData.walkerType;
// 	medicalDataObject.poopScore = poopAndPeeScore.poopScore;
// 	medicalDataObject.peeScore = poopAndPeeScore.peeScore;

// 	return medicalDataObject;
// }

// /**
//  * A function to prepare the data we nede to post to the server
//  * in order to save the enrichment to mongo
//  *
//  * @export
//  * @param {animal Object} animal
//  * @returns object containing info about the enrichment, ready to send to server
//  */
// export function logEnrichmentData(
// 	animal,
// 	extraData = { emoticon: null, comment: null }
// ) {
// 	//gets the day/month/year ref in the enrichment section of the database

// 	let timeReturned = moment();

// 	let minutesOfEnrichment = timeReturned.diff(
// 		animal.signOutData.timeTakenOut,
// 		'minutes'
// 	);

// 	let signOutData = {
// 		aNumber: animal.aNumber
// 	};
// 	Object.assign(signOutData, animal.signOutData);
// 	signOutData.minutesOfEnrichment = minutesOfEnrichment;
// 	signOutData.timeReturned = timeReturned.format();
// 	if (animal.animalType === 'cat') {
// 		signOutData.emoticon = extraData.emoticon;
// 		signOutData.comment = extraData.comment;
// 	}
// 	//stores data in enrich data obj (organized by date)
// 	return signOutData;
// }

// export async function getAnimal(aNumber) {
// 	try {
// 		const res = await prefilledFetch({
// 			endpoint: `/animals/${aNumber}`,
// 			method: 'GET',
// 			body: {}
// 		});

// 		if (badStatus(res.status)) {
// 			const err = await res.json();
// 			throw new Error(err.message ? err.message : err);
// 		}

// 		return await res.json();
// 	} catch (error) {
// 		await errorAlert(error.message);
// 	}
// }

// export async function apiCall(options: PrefilledFetchOptions) {
//   const res = await prefilledFetch(options);

//   if (badStatus(res.status)) {
//     const err = await res.json();
//     throw new Error(err.message ? err.message : err);
//   }

//   const parsedJson = await res.json();

//   return parsedJson;
// }

// /**
//  * Switched an animal from active to inactive, clears signoutData,
//  * and optionally updates the last out enrich type if that animal was
//  * signed out previously. Should be called as part of toggltAnimalActive()
//  * @param {animal Object} animal
//  * @param {boolean} updateLastEnrichType
//  */
// export const toggleAnimalActive = async function(animal: Animal) {
// 	try {
// 		const res = await prefilledFetch({
// 			endpoint: `/animals/${animal.aNumber}`,
// 			method: 'PATCH',
// 			body: {
// 				active: !animal.active,
// 				currentlyOut: false,
// 				signOutData: null,
// 			// If the animal is being signed back in before updating, update the last out enrich type as well
// 			}
// 		});

// 		if (badStatus(res.status)) {
// 			const err = await res.json();
// 			throw new Error(err);
// 		}

// 		let successTitle;
// 		let successSentance;

// 		// counterintuitive, but !animal.active will reflect the NEW state of the animal,
// 		// so if the animal WAS inactive, he is now active... so animal.active would be false,
// 		// but !animal.active is true
// 		if (!animal.active) {
// 			successTitle = `is now active!`;
// 			successSentance = `can now be found on the enrichment list.`;
// 		} else {
// 			successTitle = `is no longer active!`;
// 			successSentance = `will no longer appear on the enrichment list, but will remain in the database.`;
// 		}

// 		callback(res);
// 		const success = await sweetAlert({
// 			title: `${animal.name} ${successTitle}`,
// 			text: `${animal.name} ${successSentance}`,
// 			type: 'success',
// 			confirmButtonText: 'Okay'
// 		});
// 	} catch (error) {
// 		errorAlert(error.message);
// 	}
// };

// /**
//  * Take a firebase array-like obect, and converts it into
//  * an array of objects, containing the key and the contents
//  * of that array entry in content. Add a / to the end of ref passed in!
//  * @param {Firebase array object} object
//  * @param {string} firebase database location (ref)
//  * @returns {array} An iteratable array
//  */
// export function arrayify(object, ref = '') {
// 	let result = [];
// 	for (let key in object) {
// 		result.push({
// 			key,
// 			content: object[key],
// 			ref: ref + key
// 		});
// 	}
// 	return result;
// }

/**
 * sends a reset password email to the supplied email address
 * @param {string} email
 */
export function resetFirebasePassword(email: string): Promise<string> {
  return new Promise(resolve => {
    const auth = firebase.auth();
    auth
      .sendPasswordResetEmail(email)
      .then(() => {
        return successAlert('Email Sent', `A password reset email has been sent to ${email}.`);
      })
      .then(() => resolve(''))
      .catch(error => {
        if (error && error.message) {
          return resolve(error.message);
        }
        errorAlert(error);
        resolve(error);
      });
  });
}

// /**
//  *
//  * Pulls the enrichment data between two dates from firebase
//  *
//  * @param {string} Date: YYYY/MMM/DD
//  * @param {string} Date: YYYY/MMM/DD
//  * @returns {Promise} Promise, which when resolved contains an array of firebase responses
//  */
// export function getEnrichmentDataBetweenDates(startDate, endDate) {
// 	let [startYear, startMonth, startDay] = startDate.split('/');
// 	let [endYear, endMonth, endDay] = endDate.split('/');
// 	let months = [
// 		'Jan',
// 		'Feb',
// 		'Mar',
// 		'Apr',
// 		'May',
// 		'Jun',
// 		'Jul',
// 		'Aug',
// 		'Sep',
// 		'Oct',
// 		'Nov',
// 		'Dec'
// 	];
// 	//figure out how much data we need
// 	let yearsToFetch = parseInt(endYear, 10) - parseInt(startYear, 10) + 1; // 2017 - 2017 = 1 year, ergo add 1
// 	let monthsToFetch =
// 		12 * yearsToFetch -
// 		months.indexOf(startMonth) -
// 		(12 - months.indexOf(endMonth)) +
// 		1; //minimum would be 1

// 	//Prep the array we will call from later
// 	const refsToFetch = [];

// 	//for each year
// 	for (let i = 0; i < yearsToFetch; i++) {
// 		let year = parseInt(startYear, 10) + i;
// 		months.forEach((month, j) => {
// 			//if the year we are on is the start year...
// 			if (parseInt(year, 10) === parseInt(startYear, 10)) {
// 				//if the end year and the start year are the same...
// 				if (parseInt(endYear, 10) === parseInt(startYear, 10)) {
// 					//only add the months after and including the month requested. ignore the months before
// 					if (j >= months.indexOf(startMonth) && j <= months.indexOf(endMonth)) {
// 						refsToFetch.push(firebaseRef(`enrichmentData/${year}/${month}`));
// 					}
// 				} else if (j >= months.indexOf(startMonth)) {
// 					//if we're doing multiple years, grab the month requested and the months that come after
// 					refsToFetch.push(firebaseRef(`enrichmentData/${year}/${month}`));
// 				}
// 			} else if (parseInt(year, 10) === parseInt(endYear, 10)) {
// 				//if we're dealing with the end year, we only want the months up to and including the last month requested
// 				if (j <= months.indexOf(endMonth)) {
// 					refsToFetch.push(firebaseRef(`enrichmentData/${year}/${month}`));
// 				}
// 			} else {
// 				//if this is an inbetween year, get every month.
// 				refsToFetch.push(firebaseRef(`enrichmentData/${year}/${month}`));
// 			}
// 		});
// 	}
// 	const promises = refsToFetch.map(ref => ref.once('value'));

// 	return Promise.all(promises);
// }

// /**
//  *
//  * returns the average enrichment times for the day passed in.
//  *
//  * @param {enrichmentData day object} dayObject
//  * @returns {object} various averages from the day
//  */
// export function getAverageEnrichTime(dayObject) {
// 	let total = 0;
// 	let numberOfEnrichments = 0;
// 	let numberOfAnimals = 0;
// 	for (let animal in dayObject) {
// 		let currentAnimal = dayObject[animal];
// 		if (animal !== 'origin') {
// 			numberOfAnimals = numberOfAnimals + 1;
// 			for (let enrich in currentAnimal) {
// 				let currentEnrich = currentAnimal[enrich];
// 				total = total + currentEnrich.minutesOfEnrichment;
// 				numberOfEnrichments = numberOfEnrichments + 1;
// 			}
// 		}
// 	}
// 	return {
// 		'average enrichment per animal per day': total / numberOfAnimals,
// 		'average length of single enrichment session': total / numberOfEnrichments,
// 		'total': total
// 	};
// }

interface PrefilledFetchOptions {
  method: 'GET' | 'POST' | 'DELETE' | 'PATCH';
  body: { [key: string]: any };
  endpoint: string;
}

/**
 * Save me some typing when creating a fetch
 * @param {Object containing the method(string) and body(object) to fetch} param0
 */
export function prefilledFetch({ method, body, endpoint }: PrefilledFetchOptions) {
  let preparedBody;
  let contentType = '';
  let preparedEndpoint;
  if (method.toLowerCase() === 'get' || method.toLowerCase() === 'delete') {
    preparedBody = undefined;
    if (body) {
      const query = new URLSearchParams(body);
      preparedEndpoint = `${endpoint}?${query.toString()}`;
    } else {
      preparedEndpoint = endpoint;
    }
    contentType = 'application/x-www-form-urlencoded;charset=UTF-8';
  } else if (method.toLowerCase() === 'post' || method.toLowerCase() === 'put' || method.toLowerCase() === 'patch') {
    preparedBody = JSON.stringify(body);
    contentType = 'application/json';
    preparedEndpoint = endpoint;
  }

  const headers = new Headers();
  headers.append('Content-Type', contentType);

  return fetch(`${serverAddress}${preparedEndpoint}`, {
    method,
    headers,
    body: preparedBody,
  });
}

export function handleFetchError(err: Error) {
  errorAlert(err.message);
}

export function handleFetchInitialResponse(res: Response) {
  if (res.status !== 200) {
    throw new Error(`Error saving data to mongo! Code: ${res.status}`);
  } else {
    return res.json();
  }
}

/**
 * Takes a string of minutes, or number of minutes (1.5, 2, etc) and returns a
 * string explaining the amount of time.
 * @param {String} minutes to parse
 */
export function printMinutesAndSeconds(min: string | number) {
  const int = typeof min === 'string' ? parseFloat(min) : min;
  const minutes = Math.floor(int);
  const seconds = (int * 60) % 60;
  return `${minutes} minutes and ${seconds} seconds`;
}

export const uniqueOnlyFilter = (item: any, pos: number, self: any[]) => self.indexOf(item) === pos;

export function breakdownEnrichmentTypes(data: Enrichment[]) {
  const enrichmentTypeData: { [Key: string]: any } = {};

  data.forEach(entry => {
    // Pull out data for the pie graph (totals by type) (ie add this dogs walk time to "walk" in the object, to eventually have a total)
    if (enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] === undefined) {
      // if there is no entry for an enrichment type, set it to zero
      enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] = 0;
    }
    enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] =
      enrichmentTypeData[entry.enrichmentType.toLowerCase().trim()] + entry.minutesOfEnrichment;
  });
  return enrichmentTypeData;
}
